import React, { useState } from 'react';
import { Users, Building, Presentation, ArrowRight, Sparkles, Rocket, CheckCircle } from 'lucide-react';
import { services } from '../mockData';
import ServiceModal from './ServiceModal';

const iconMap = {
  Users,
  Building,
  Presentation
};

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
  };

  return (
    <section id="services" className="section section-cream">
      <div className="container">
        <div className="text-center mb-6">
          <div className="flex" style={{ alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <h2 className="heading-1 mb-0">Mes Services</h2>
          </div>
          <span className="section-label" style={{ display: 'block', marginBottom: '0' }}>Ce que je propose</span>
          <p className="body-large text-muted" style={{ maxWidth: '600px', margin: '0 auto' }}>
            Accompagnement sur-mesure adapté à votre profil et vos objectifs patrimoniaux
          </p>
        </div>

        <div className="grid-3">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            const hasGradientBg = index === 1; // Card du milieu avec gradient
            
            return (
              <div 
                key={service.id} 
                className="card-service"
                style={{
                  background: hasGradientBg 
                    ? 'linear-gradient(135deg, rgba(172, 153, 105, 0.1) 0%, rgba(255, 255, 255, 0.95) 100%)'
                    : 'var(--bg-card)',
                  transform: 'translateY(0)',
                  transition: 'all 0.4s ease',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(172, 153, 105, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.05)';
                }}
              >
                {/* Decorative element */}
                <div style={{
                  position: 'absolute',
                  top: '-50px',
                  right: '-50px',
                  width: '100px',
                  height: '100px',
                  background: 'linear-gradient(45deg, rgba(172, 153, 105, 0.1), transparent)',
                  borderRadius: '50%',
                  zIndex: 0
                }}></div>

                <div className="mb-4" style={{ position: 'relative', zIndex: 1 }}>
                  <div className="icon-tile icon-tile-shimmer" style={{
                    width: '70px',
                    height: '70px',
                    borderRadius: '1rem',
                    background: `linear-gradient(135deg, var(--primary-gold) 0%, var(--primary-brown) 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.5rem',
                    boxShadow: '0 8px 24px rgba(172, 153, 105, 0.3)'
                  }}>
                    <IconComponent size={32} color="white" />
                  </div>
                  <h3 className="heading-2" style={{ marginBottom: '0.5rem' }}>{service.title}</h3>
                  <p className="body-small" style={{ 
                    color: 'var(--primary-gold)', 
                    fontWeight: '600',
                    marginBottom: '1rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontSize: '0.75rem'
                  }}>
                    {service.subtitle}
                  </p>
                </div>

                <p className="body-medium mb-4" style={{ position: 'relative', zIndex: 1 }}>
                  {service.description}
                </p>

                <div className="mb-6" style={{ position: 'relative', zIndex: 1 }}>
                  {service.features.map((feature, featureIndex) => (
                    <div 
                      key={featureIndex} 
                      className="flex" 
                      style={{ 
                        alignItems: 'center', 
                        gap: '0.75rem', 
                        marginBottom: '0.75rem',
                        padding: '0.5rem',
                        borderRadius: '0.5rem',
                        background: featureIndex % 2 === 0 ? 'transparent' : 'rgba(172, 153, 105, 0.05)',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: 'linear-gradient(45deg, var(--primary-gold), var(--primary-brown))',
                        boxShadow: '0 2px 6px rgba(172, 153, 105, 0.3)'
                      }}></div>
                      <span className="body-small" style={{ fontWeight: '500' }}>{feature}</span>
                    </div>
                  ))}
                </div>

                <button 
                  onClick={() => openModal(service)}
                  className="btn-secondary" 
                  style={{ 
                    marginTop: 'auto',
                    width: '100%',
                    background: 'linear-gradient(135deg, transparent 0%, rgba(172, 153, 105, 0.1) 100%)',
                    border: '2px solid var(--primary-gold)',
                    fontWeight: '600',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  En savoir plus
                  <ArrowRight size={16} />
                </button>
              </div>
            );
          })}
        </div>

        {/* Encart Recrutement */}
        <div className="mt-8" style={{ maxWidth: '900px', margin: '3rem auto 0' }}>
          <div 
            className="card-service"
            style={{
              background: 'linear-gradient(135deg, rgba(58, 46, 15, 0.03) 0%, rgba(172, 153, 105, 0.08) 100%)',
              border: '2px solid rgba(172, 153, 105, 0.2)',
              position: 'relative',
              overflow: 'hidden',
              padding: '2rem 2.5rem'
            }}
          >
            <div style={{
              position: 'absolute',
              top: '-40px',
              right: '-40px',
              width: '120px',
              height: '120px',
              background: 'linear-gradient(45deg, rgba(172, 153, 105, 0.1), transparent)',
              borderRadius: '50%'
            }}></div>

            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '0.75rem',
                  background: 'linear-gradient(135deg, var(--primary-gold), var(--primary-brown))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(172, 153, 105, 0.3)'
                }}>
                  <Rocket size={24} color="white" />
                </div>
                <div>
                  <h3 className="heading-2" style={{ marginBottom: '0.15rem' }}>Recrutement</h3>
                  <p style={{ color: 'var(--primary-gold)', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
                    Saisir l'opportunité
                  </p>
                </div>
              </div>

              <p className="body-medium" style={{ marginBottom: '1.25rem', marginTop: '1rem' }}>
                Je forme et accompagne des indépendants dans le lancement et le développement de leur activité de CGP dans un cadre professionnel, sécurisé et performant.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0.5rem', marginBottom: '1.5rem' }}>
                {[
                  "Formation certifiante & plan de lancement personnalisé",
                  "Accès à un univers complet de solutions d'investissement",
                  "Modèle économique transparent et scalable",
                  "Pilotage de l'activité avec outils et indicateurs clés",
                  "Univers clients & professionnel innovant"
                ].map((item, i) => (
                  <div key={i} className="flex" style={{ alignItems: 'center', gap: '0.5rem' }}>
                    <CheckCircle size={16} style={{ color: 'var(--primary-gold)', flexShrink: 0 }} />
                    <span className="body-small" style={{ fontWeight: '500' }}>{item}</span>
                  </div>
                ))}
              </div>

              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                <p style={{ 
                  fontSize: '1.15rem', 
                  fontWeight: '600', 
                  color: 'var(--primary-brown)',
                  fontStyle: 'italic',
                  marginBottom: '1rem'
                }}>
                  Et si c'était fait pour vous ?
                </p>
                <a 
                  href="https://calendly.com/arthur-vacogne-rdv/rdv-echange-1er-prise-de-contact" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-primary"
                  data-testid="recruitment-cta-btn"
                >
                  Discutons-en
                  <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action - Plus moderne */}
        <div className="text-center mt-8">
          <div className="card" style={{ 
            maxWidth: '500px', 
            margin: '0 auto',
            background: 'linear-gradient(135deg, var(--primary-gold) 0%, var(--primary-brown) 100%)',
            color: 'white',
            border: 'none',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-20px',
              left: '-20px',
              width: '80px',
              height: '80px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50%'
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              right: '-30px',
              width: '100px',
              height: '100px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '50%'
            }}></div>

            <div style={{ position: 'relative', zIndex: 1 }}>
              <h3 className="heading-3 mb-2" style={{ color: 'white' }}>
                Besoin d'un conseil personnalisé ?
              </h3>
              <p className="body-medium mb-4" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                Chaque situation est unique. Parlons de vos objectifs lors d'un entretien gratuit.
              </p>
              <a 
                href="https://calendly.com/arthur-vacogne-rdv/rdv-echange-1er-prise-de-contact" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-secondary"
                style={{ 
                  background: 'white',
                  color: 'var(--primary-brown)',
                  border: 'none',
                  fontWeight: '600'
                }}
              >
                Prendre rendez-vous
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      <ServiceModal 
        service={selectedService}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </section>
  );
};

export default Services;